import React, { useState } from "react";
import {
  ClipboardList,
  Eye,
  Plus,
  Mail,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePagination } from "@/components/ui/table-pagination";
import {
  Questionnaire,
  usePatienQuestionnaireData,
} from "../../../lib/dashboard/questionnairesData";
import { PreConsultTemplate } from "@/lib/settings/preConsultData";
import { SendQuestionnaireModal } from "../components/SendQuestionnaireModal";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseConsultationUpsert } from "@/data/constantStrings";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getVisiblePages } from "@/lib/utils";

interface PatientQuestionnairesPanelProps {
  questionnaires: Questionnaire[];
  patientEmail: string;
  patientName: string;
  preConsultTemplates: PreConsultTemplate[];
  isDarkMode: boolean;
  onDeleteQuestionnaire: (id: string) => void;
  onSendQuestionnaire: (
    questionnaire: Questionnaire,
    message: string,
    subject: string,
    email: string,
  ) => void;
  onStartConsultation?: (questionnaire: Questionnaire) => void;
  setPatientEmail: (email: string) => void;
}

export function PatientQuestionnairesPanel({
  questionnaires,
  patientName,
  preConsultTemplates,
  isDarkMode,
  onDeleteQuestionnaire,
  onSendQuestionnaire,
  onStartConsultation,
  patientEmail,
  setPatientEmail,
}: PatientQuestionnairesPanelProps) {
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<Questionnaire | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  // Pagination setup - 5 items per page to match other panels
  const pagination = usePagination(5);
  const { currentPage, itemsPerPage, handlePageChange, getPaginatedData } =
    pagination;
  const { startNewConsultation } = usePatienQuestionnaireData();

  const handleOpenSendModal = () => {
    // This ensures the patientEmail is set when modal opens
    if (patientEmail) {
      setPatientEmail(patientEmail);
    }
    setSendModalOpen(true);
  };

  // Calculate pagination
  const { paginatedData: paginatedQuestionnaires, totalPages } =
    getPaginatedData(
      [...questionnaires].sort(
        (a, b) =>
          new Date(b.completedDate).getTime() -
          new Date(a.completedDate).getTime(),
      ),
    );

  const handleViewQuestionnaire = (questionnaire: Questionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setViewDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return isDarkMode
          ? "bg-green-900/30 text-green-300 border border-green-700"
          : "bg-green-100 text-green-700";
      case "pending":
        return isDarkMode
          ? "bg-orange-900/30 text-orange-300 border border-orange-700"
          : "bg-orange-100 text-orange-700";
      default:
        return isDarkMode
          ? "bg-gray-900/30 text-gray-300 border border-gray-700"
          : "bg-gray-100 text-gray-700";
    }
  };

  return (
    <>
      <div
        className={`border rounded-xl p-6 transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ClipboardList
              className={`w-5 h-5 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            />
            <h3
              className={`text-sm font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Questionnaires
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="secondary"
              className={`text-xs ${
                isDarkMode
                  ? "bg-gray-700 text-gray-300"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {questionnaires.length}
            </Badge>
            <Button
              size="sm"
              onClick={handleOpenSendModal}
              className="text-white bg-blue-600 hover:bg-blue-700"
              title="Send questionnaire to patient"
            >
              <Mail className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {questionnaires.length === 0 ? (
            <div
              className={`text-center py-8 border rounded-lg transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-900/50 border-gray-600 text-gray-400"
                  : "bg-gray-50 border-gray-200 text-gray-500"
              }`}
            >
              <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No questionnaires completed</p>
              <p className="mt-1 text-xs opacity-75">
                Patient has not filled out any forms yet
              </p>
            </div>
          ) : (
            paginatedQuestionnaires.map((questionnaire) => (
              <div
                key={questionnaire.id}
                onClick={() => handleViewQuestionnaire(questionnaire)}
                className={`p-3 border rounded-lg cursor-pointer group transition-all duration-300 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 hover:bg-gray-600 hover:border-gray-500"
                    : "bg-gray-50 border-gray-200 hover:bg-white hover:border-gray-300 hover:shadow-sm"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-1">
                      <ClipboardList
                        className={`w-4 h-4 flex-shrink-0 mt-0.5 transition-colors duration-300 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-medium transition-colors duration-300 line-clamp-1 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {questionnaire.name}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {new Date(
                              questionnaire.completedDate ??
                                questionnaire.createdAt,
                            ).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                          {/* <span
                            className={`text-xs ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            •
                          </span>
                          <Badge variant="outline" className="h-5 text-xs">
                            {questionnaire.category}
                          </Badge> */}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center flex-shrink-0 gap-2">
                    <Badge
                      className={`text-xs capitalize ${getStatusColor(
                        questionnaire.status,
                      )}`}
                    >
                      {questionnaire.status}
                    </Badge>
                    <Eye
                      className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${
                        isDarkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {questionnaires.length > itemsPerPage && totalPages > 1 && (
          <div
            className={`flex items-center justify-between mt-4 pt-3 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div
              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, questionnaires.length)} of{" "}
              {questionnaires.length}
            </div>

            <div className="flex flex-wrap items-center max-w-full gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-1 rounded transition-colors duration-300 ${
                  currentPage === 1
                    ? isDarkMode
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 cursor-not-allowed"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {(() => {
                const { start, end } = getVisiblePages(
                  currentPage,
                  totalPages,
                  5,
                ); // max 5 visible

                return (
                  <>
                    {start > 1 && (
                      <>
                        <button
                          onClick={() => handlePageChange(1)}
                          className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                        >
                          1
                        </button>
                        <span className="px-1 text-xs text-gray-400">…</span>
                      </>
                    )}

                    {Array.from(
                      { length: end - start + 1 },
                      (_, i) => start + i,
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          page === currentPage
                            ? isDarkMode
                              ? "bg-blue-600 text-white"
                              : "bg-blue-500 text-white"
                            : isDarkMode
                              ? "text-gray-300 hover:bg-gray-700"
                              : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    {end < totalPages && (
                      <>
                        <span className="px-1 text-xs text-gray-400">…</span>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </>
                );
              })()}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-1 rounded transition-colors duration-300 ${
                  currentPage === totalPages
                    ? isDarkMode
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 cursor-not-allowed"
                    : isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Questionnaire Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent
          className={`max-w-2xl max-h-[80vh] overflow-y-auto ${
            isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white"
          }`}
        >
          <DialogHeader>
            <DialogTitle
              className={isDarkMode ? "text-white" : "text-gray-900"}
            >
              {selectedQuestionnaire?.name}
            </DialogTitle>
            <div
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Completed on{" "}
              {selectedQuestionnaire &&
                new Date(
                  selectedQuestionnaire.completedDate,
                ).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}{" "}
              by {selectedQuestionnaire?.completedBy}
            </div>
          </DialogHeader>

          {selectedQuestionnaire && (
            <div className="mt-4 space-y-4">
              {/* Category and Status */}
              <div className="flex items-center gap-2">
                <Badge
                  className={`text-xs capitalize ${getStatusColor(
                    selectedQuestionnaire.status,
                  )}`}
                >
                  {selectedQuestionnaire.status}
                </Badge>
              </div>

              {/* Responses */}
              {selectedQuestionnaire.responses &&
                selectedQuestionnaire.responses.length > 0 && (
                  <div className="space-y-4">
                    <h4
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Responses
                    </h4>
                    {selectedQuestionnaire.responses
                      ?.filter(
                        (r) =>
                          !r.question?.toLowerCase().includes("signature") &&
                          !r.answer?.startsWith("data:image"),
                      )
                      .map((response, index) => (
                        <div
                          key={index}
                          className={`p-4 border rounded-lg ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-700"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div
                            className={`text-sm font-medium mb-2 ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {response.question}
                          </div>
                          <div
                            className={`text-sm ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {response.answer}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

              {/* Notes */}
              {selectedQuestionnaire.notes && (
                <div>
                  <h4
                    className={`font-medium mb-2 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Notes
                  </h4>
                  <div
                    className={`p-4 border rounded-lg ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-gray-300"
                        : "bg-gray-50 border-gray-200 text-gray-700"
                    }`}
                  >
                    {selectedQuestionnaire.notes}
                  </div>
                </div>
              )}

              {/* Score if available */}
              {selectedQuestionnaire.score && (
                <div>
                  <h4
                    className={`font-medium mb-2 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Score
                  </h4>
                  <div
                    className={`p-4 border rounded-lg ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white"
                        : "bg-gray-50 border-gray-200 text-gray-900"
                    }`}
                  >
                    {selectedQuestionnaire.score}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer with Start Consultation button */}
          {selectedQuestionnaire &&
            selectedQuestionnaire.status === "completed" &&
            onStartConsultation && (
              <DialogFooter className="mt-6">
                <Button
                  onClick={() => {
                    const hasConsultPermission = checkUserPermissions(
                      accessTitanverseConsultationUpsert,
                    );

                    if (!hasConsultPermission) {
                      setViewDialogOpen(false);
                      setShowPermissionMessage(
                        "You don't have permission to start consultation.",
                      );
                      setShowPermissionModal(true);
                      return;
                    }

                    onStartConsultation(selectedQuestionnaire);
                    setViewDialogOpen(false);
                    startNewConsultation(selectedQuestionnaire?.id);
                  }}
                  className={`text-white ${
                    checkUserPermissions(accessTitanverseConsultationUpsert)
                      ? "bg-slate-900 hover:bg-slate-800"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Start Consultation from this Questionnaire
                </Button>
              </DialogFooter>
            )}
        </DialogContent>
      </Dialog>

      {/* Send Questionnaire Modal */}
      <SendQuestionnaireModal
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        patientName={patientName}
        preConsultTemplates={preConsultTemplates}
        isDarkMode={isDarkMode}
        onSendQuestionnaire={onSendQuestionnaire}
        patientEmail={patientEmail}
        setPatientEmail={setPatientEmail}
      />
    </>
  );
}
